import { useCallback, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowUpRight, ChevronLeft, ChevronRight, X } from 'lucide-react';

const Lightbox = ({ item, title, onClose, onPrev, onNext, cta = null }) => {
    const handleKey = useCallback(
        (e) => {
            if (e.key === 'Escape') onClose();
            if (e.key === 'ArrowLeft') onPrev();
            if (e.key === 'ArrowRight') onNext();
        },
        [onClose, onPrev, onNext]
    );

    useEffect(() => {
        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    }, [handleKey]);

    return (
        <AnimatePresence>
            {item && (
                <motion.div
                    className="fixed inset-0 z-[130] bg-ink/95 backdrop-blur-md flex flex-col"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    data-testid="lightbox"
                >
                    <button
                        onClick={onClose}
                        className="absolute top-6 right-6 z-10 flex items-center justify-center w-12 h-12 rounded-full border border-white/20 text-paper transition-colors duration-300 hover:border-dusk hover:text-dusk"
                        aria-label="Close"
                        data-testid="lightbox-close-button"
                    >
                        <X className="w-5 h-5" />
                    </button>
                    <button
                        onClick={onPrev}
                        className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center w-12 h-12 rounded-full border border-white/20 text-paper transition-colors duration-300 hover:border-dusk hover:text-dusk"
                        aria-label="Previous"
                        data-testid="lightbox-prev-button"
                    >
                        <ChevronLeft className="w-5 h-5" />
                    </button>
                    <button
                        onClick={onNext}
                        className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center w-12 h-12 rounded-full border border-white/20 text-paper transition-colors duration-300 hover:border-dusk hover:text-dusk"
                        aria-label="Next"
                        data-testid="lightbox-next-button"
                    >
                        <ChevronRight className="w-5 h-5" />
                    </button>
                    <div className="flex-1 flex items-center justify-center px-6 py-20 min-h-0" onClick={onClose}>
                        <motion.img
                            key={item}
                            src={`/images/${item}.jpg`}
                            alt={title}
                            className="max-h-full max-w-full object-contain shadow-2xl shadow-black"
                            initial={{ opacity: 0, scale: 0.96 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                            onClick={(e) => e.stopPropagation()}
                            data-testid="lightbox-image"
                        />
                    </div>
                    <div className="pb-10 text-center flex flex-col items-center gap-5">
                        <p className="font-serif italic text-2xl text-paper tracking-tight lowercase" data-testid="lightbox-caption">{title}</p>
                        {cta && (
                            <button
                                type="button"
                                onClick={cta.onClick}
                                className="group inline-flex items-center gap-2 rounded-full border border-white/25 px-6 py-2.5 text-xs tracking-[0.2em] uppercase text-paper transition-colors duration-300 hover:border-dusk hover:text-dusk"
                                data-testid="lightbox-cta"
                            >
                                {cta.label}
                                <ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                            </button>
                        )}
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default Lightbox;
